import { ReactNode } from 'react';

/**
 * The two tab strips this app has, defined once each.
 *
 * There were THIRTEEN hand-rolled strips in four visual languages — underline
 * in three different blues, underline in indigo, segmented pill, and solid-fill
 * pill — so which one you got depended on which screen you happened to be on,
 * and two of them sat on top of each other on `/settings`.
 *
 * TWO is the right number, because they mean different things:
 *
 *   <Tabs>    switches the CONTENT of a screen. Underline, sits on a rule.
 *   <SubTabs> switches between siblings INSIDE a tab. Segmented pill, inset.
 *
 * Both are presentation only. Where the selection is kept — a search param, a
 * route segment, component state — stays with the caller, because that is a
 * question about what should be linkable and the answer differs per screen.
 */

export interface TabDef<K extends string = string> {
  key: K;
  label: ReactNode;
  /** Tooltip. Worth setting where the label has to be short. */
  hint?: string;
  /** A count or status, rendered dimmer and after the label. */
  badge?: ReactNode;
  /** Offer a × that closes it (SubTabs with `onClose` only). */
  closable?: boolean;
}

interface TabsProps<K extends string> {
  tabs: TabDef<K>[];
  active: K;
  onChange: (key: K) => void;
  className?: string;
  /** Shorter rows, for a strip that shares a header line with other controls
   *  (the boardroom's Reports / Feed sits beside the crumbs and Back). */
  dense?: boolean;
}

/**
 * Top-level tabs for a screen. `overflow-y-hidden` is load-bearing:
 * `overflow-x-auto` alone computes overflow-y to `auto` rather than leaving it
 * visible, and the `-mb-px` that laps the active underline over the container's
 * border puts the strip 1px past the content box — enough for a stray vertical
 * scrollbar.
 */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  className = 'mb-4',
  dense = false,
}: TabsProps<K>) {
  return (
    <div
      className={`border-b border-gray-200 dark:border-gray-700 overflow-x-auto overflow-y-hidden ${className}`}
    >
      <nav className={`-mb-px flex ${dense ? 'gap-4' : 'gap-4 sm:gap-6'} min-w-max text-sm font-medium`}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            title={t.hint}
            aria-current={active === t.key ? 'page' : undefined}
            className={`-mb-px border-b-2 px-1 ${dense ? 'py-1' : 'py-3'} inline-flex items-center gap-1.5 whitespace-nowrap ${
              active === t.key
                ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <span>{t.label}</span>
            {t.badge != null && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{t.badge}</span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}

interface SubTabsProps<K extends string> {
  tabs: TabDef<K>[];
  active: K;
  onChange: (key: K) => void;
  /** One line on what this group of siblings is for. The pill labels name the
   *  children; nothing named the parent, so landing on a hub from a deep-link
   *  showed a row of pills and no clue what they belonged to. */
  description?: ReactNode;
  className?: string;
  /** Close a tab marked `closable`. Where it can be brought back from is the
   *  caller's business. */
  onClose?: (key: K) => void;
}

/** Second-level tabs, inside a screen that already has `Tabs`. */
export function SubTabs<K extends string>({
  tabs,
  active,
  onChange,
  description,
  className = 'mb-5',
  onClose,
}: SubTabsProps<K>) {
  return (
    <div className={className}>
      {description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{description}</p>
      )}
      <div className="inline-flex flex-wrap items-center gap-0.5 rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5 border border-gray-200 dark:border-gray-700">
        {tabs.map((t) => {
          const tone =
            active === t.key
              ? 'bg-white dark:bg-gray-900 shadow-sm text-gray-900 dark:text-gray-100 font-medium'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200';
          if (!(onClose && t.closable)) {
            return (
              <button
                key={t.key}
                onClick={() => onChange(t.key)}
                title={t.hint}
                aria-current={active === t.key ? 'page' : undefined}
                className={`px-4 py-1.5 text-sm rounded-md transition-colors inline-flex items-center gap-1.5 ${tone}`}
              >
                <span>{t.label}</span>
                {t.badge != null && t.badge}
              </button>
            );
          }
          // A CLOSABLE TAB: the same pill, with a × of its own beside the label.
          return (
            <span key={t.key} className={`group rounded-md transition-colors inline-flex items-center ${tone}`}>
              <button
                onClick={() => onChange(t.key)}
                title={t.hint}
                aria-current={active === t.key ? 'page' : undefined}
                className="pl-4 pr-1 py-1.5 text-sm inline-flex items-center gap-1.5"
              >
                <span>{t.label}</span>
                {t.badge != null && t.badge}
              </button>
              <button
                type="button"
                onClick={() => onClose(t.key)}
                aria-label={`Close ${typeof t.label === 'string' ? t.label : t.key}`}
                title="Close this tab - bring it back from + more"
                className="icon-btn pr-2 pl-0.5 py-1.5 text-gray-400 opacity-60 hover:text-gray-700 group-hover:opacity-100 dark:hover:text-gray-200"
                data-testid={`tab-close-${t.key}`}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
